import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../AxiosInstance';
import {useTranslation} from "react-i18next";

const FIELD_DEFS = [
    { key: 'name',        labelKey: 'maintenance.import.columns.name',        required: true,  synonyms: ['name','nombre','description','part','pieza','item','producto'] },
    { key: 'brand',       labelKey: 'maintenance.import.columns.brand',       required: false, synonyms: ['brand','marca','manufacturer','fabricante'] },
    { key: 'model',       labelKey: 'maintenance.import.columns.model',       required: false, synonyms: ['model','modelo'] },
    { key: 'part_number', labelKey: 'maintenance.import.columns.partNumber',  required: false, synonyms: ['part_number','p/n','pn','ref','referencia','codigo','code'] },
    { key: 'description', labelKey: 'maintenance.import.columns.description', required: false, synonyms: ['caracteristicas','specifications','specs','obs','description','descripcion'] },
    { key: 'stock',       labelKey: 'maintenance.import.columns.stock',       required: false, synonyms: ['stock','qty','quantity','cantidad','existencias'] },
    { key: 'unit_cost',   labelKey: 'maintenance.import.columns.unitCost',    required: false, synonyms: ['unit_cost','cost','price','precio','costo','pvp'] },
];

function autoMap(headers) {
    const mapping = {};
    FIELD_DEFS.forEach(field => {
        const lowerHeaders = headers.map(h => h.toLowerCase().trim());
        const match = field.synonyms.find(syn => lowerHeaders.includes(syn));
        if (match) {
            const idx = lowerHeaders.indexOf(match);
            mapping[field.key] = headers[idx];
        } else {
            mapping[field.key] = '';
        }
    });
    return mapping;
}

const STEP_UPLOAD  = 1;
const STEP_MAP     = 2;
const STEP_PREVIEW = 3;
const STEP_RESULT  = 4;

export default function PartsImportModal({ show, onClose, projectId, machineId }) {
    const {t} = useTranslation();
    const [step, setStep] = useState(STEP_UPLOAD);
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);
    const [mapping, setMapping] = useState({});
    const [preview, setPreview] = useState([]);
    const [result, setResult] = useState(null);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef();

    if (!show) return null;

    const reset = () => {
        setStep(STEP_UPLOAD);
        setHeaders([]);
        setRows([]);
        setMapping({});
        setPreview([]);
        setResult(null);
        setError('');
    };

    const handleClose = () => { reset(); onClose(); };

    // Step 1: parse file
    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setError('');
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const wb = XLSX.read(ev.target.result, { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
                if (data.length < 2) { setError(t('maintenance.import.errorNotEnough')); return; }
                const hdrs = data[0].map(String);
                const dataRows = data.slice(1).filter(r => r.some(c => c !== ''));
                setHeaders(hdrs);
                setRows(dataRows);
                setMapping(autoMap(hdrs));
                setStep(STEP_MAP);
            } catch (err) {
                setError(t('maintenance.import.errorRead'));
            }
        };
        reader.readAsArrayBuffer(file);
    };

    // Step 2 → 3: build preview
    const handlePreview = () => {
        const nameCol = mapping['name'];
        if (!nameCol) { setError(t('maintenance.import.errorNameRequired')); return; }
        setError('');
        const headerIdxMap = {};
        FIELD_DEFS.forEach(f => {
            if (mapping[f.key]) {
                headerIdxMap[f.key] = headers.indexOf(mapping[f.key]);
            }
        });
        const first5 = rows.slice(0, 5).map(row => {
            const obj = {};
            FIELD_DEFS.forEach(f => {
                obj[f.key] = headerIdxMap[f.key] !== undefined ? String(row[headerIdxMap[f.key]] ?? '') : '';
            });
            return obj;
        });
        setPreview(first5);
        setStep(STEP_PREVIEW);
    };

    // Step 3 → 4: run import
    const handleImport = async () => {
        setImporting(true);
        setError('');
        try {
            const headerIdxMap = {};
            FIELD_DEFS.forEach(f => {
                if (mapping[f.key]) headerIdxMap[f.key] = headers.indexOf(mapping[f.key]);
            });
            const parts = rows.map(row => {
                const obj = {};
                FIELD_DEFS.forEach(f => {
                    obj[f.key] = headerIdxMap[f.key] !== undefined ? String(row[headerIdxMap[f.key]] ?? '') : '';
                });
                return obj;
            });
            const payload = { project_id: projectId, machine_id: machineId || null, parts };
            const res = await axiosInstance.post('spare_part/bulk_import/', payload);
            setResult(res.data);
            setStep(STEP_RESULT);
        } catch (err) {
            setError(err?.response?.data?.detail || t('maintenance.import.errorFailed'));
        } finally {
            setImporting(false);
        }
    };

    const stepLabels = [t('maintenance.import.steps.upload'), t('maintenance.import.steps.map'), t('maintenance.import.steps.preview'), t('maintenance.import.steps.result')];

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.45)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div style={{
                background: '#FFF', borderRadius: '12px', width: '680px', maxWidth: '95vw',
                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}>
                {/* Header */}
                <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #E2E8F0' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <span style={{ fontWeight: 700, fontSize: '16px', color: '#1E293B' }}>{t('maintenance.importParts')}</span>
                        <button onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#94A3B8', cursor: 'pointer', lineHeight: 1 }}>×</button>
                    </div>
                    {/* Step indicator */}
                    <div className="d-flex align-items-center" style={{ marginTop: '14px', gap: '0' }}>
                        {stepLabels.map((label, i) => {
                            const num = i + 1;
                            const active = step === num;
                            const done = step > num;
                            return (
                                <React.Fragment key={num}>
                                    <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                                        <div style={{
                                            width: '24px', height: '24px', borderRadius: '50%',
                                            background: done ? '#22C55E' : active ? '#4F46E5' : '#E2E8F0',
                                            color: done || active ? '#FFF' : '#94A3B8',
                                            fontSize: '12px', fontWeight: 700,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            {done ? '✓' : num}
                                        </div>
                                        <span style={{ fontSize: '12px', color: active ? '#4F46E5' : done ? '#22C55E' : '#94A3B8', fontWeight: active ? 600 : 400 }}>
                                            {label}
                                        </span>
                                    </div>
                                    {i < stepLabels.length - 1 && (
                                        <div style={{ flex: 1, height: '1px', background: '#E2E8F0', margin: '0 6px' }} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                    {error && (
                        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', color: '#DC2626', fontSize: '13px' }}>
                            {error}
                        </div>
                    )}

                    {/* ── Step 1: Upload ── */}
                    {step === STEP_UPLOAD && (
                        <div style={{ textAlign: 'center', paddingTop: '20px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📂</div>
                            <p style={{ color: '#64748B', fontSize: '14px', marginBottom: '20px' }}>
                                {t('maintenance.import.uploadDesc')}
                            </p>
                            <button
                                onClick={() => fileInputRef.current.click()}
                                style={{
                                    padding: '10px 24px', background: '#4F46E5', color: '#FFF',
                                    border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '14px',
                                }}
                            >
                                {t('maintenance.import.selectFile')}
                            </button>
                            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleFile} />
                        </div>
                    )}

                    {/* ── Step 2: Map columns ── */}
                    {step === STEP_MAP && (
                        <div>
                            <p style={{ color: '#64748B', fontSize: '13px', marginBottom: '16px' }}>
                                {t('maintenance.import.verifyMapping')}
                            </p>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: '#F8FAFC' }}>
                                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#334155', border: '1px solid #E2E8F0' }}>{t('maintenance.import.systemField')}</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#334155', border: '1px solid #E2E8F0' }}>{t('maintenance.import.fileColumn')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {FIELD_DEFS.map(field => (
                                        <tr key={field.key}>
                                            <td style={{ padding: '8px 12px', border: '1px solid #E2E8F0', color: '#1E293B' }}>
                                                {t(field.labelKey)}
                                                {field.required && <span style={{ color: '#DC2626', marginLeft: '3px' }}>*</span>}
                                            </td>
                                            <td style={{ padding: '8px 12px', border: '1px solid #E2E8F0' }}>
                                                <select
                                                    value={mapping[field.key] || ''}
                                                    onChange={e => setMapping({ ...mapping, [field.key]: e.target.value })}
                                                    style={{ width: '100%', padding: '5px 8px', borderRadius: '5px', border: '1px solid #E2E8F0', fontSize: '13px' }}
                                                >
                                                    <option value="">{t('maintenance.import.dontMap')}</option>
                                                    {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <p style={{ color: '#94A3B8', fontSize: '12px', marginTop: '10px' }}>
                                {t('maintenance.import.rowsFound', {count: rows.length})}
                            </p>
                        </div>
                    )}

                    {/* ── Step 3: Preview ── */}
                    {step === STEP_PREVIEW && (
                        <div>
                            <p style={{ color: '#64748B', fontSize: '13px', marginBottom: '12px' }}>
                                {t('maintenance.import.previewDesc', {count: preview.length, total: rows.length})}
                            </p>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                    <thead>
                                        <tr style={{ background: '#F8FAFC' }}>
                                            {FIELD_DEFS.filter(f => mapping[f.key]).map(f => (
                                                <th key={f.key} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#334155', border: '1px solid #E2E8F0', whiteSpace: 'nowrap' }}>
                                                    {t(f.labelKey)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.map((row, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#FFF' : '#F8FAFC' }}>
                                                {FIELD_DEFS.filter(f => mapping[f.key]).map(f => (
                                                    <td key={f.key} style={{ padding: '6px 10px', border: '1px solid #E2E8F0', color: '#1E293B', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {row[f.key] || <span style={{ color: '#CBD5E1' }}>—</span>}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {rows.length > 5 && (
                                <p style={{ color: '#94A3B8', fontSize: '12px', marginTop: '8px' }}>
                                    {t('maintenance.import.moreRows', {count: rows.length - 5})}
                                </p>
                            )}
                        </div>
                    )}

                    {/* ── Step 4: Result ── */}
                    {step === STEP_RESULT && result && (
                        <div style={{ textAlign: 'center', paddingTop: '10px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                                {result.errors?.length > 0 ? '⚠️' : '✅'}
                            </div>
                            <p style={{ fontWeight: 700, fontSize: '16px', color: '#1E293B', marginBottom: '16px' }}>
                                {t('maintenance.import.complete')}
                            </p>
                            <div className="d-flex justify-content-center gap-4" style={{ marginBottom: '20px' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#22C55E' }}>{result.created ?? 0}</div>
                                    <div style={{ fontSize: '12px', color: '#64748B' }}>{t('maintenance.import.created')}</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#3B82F6' }}>{result.updated ?? 0}</div>
                                    <div style={{ fontSize: '12px', color: '#64748B' }}>{t('maintenance.import.updated')}</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#DC2626' }}>{result.errors?.length ?? 0}</div>
                                    <div style={{ fontSize: '12px', color: '#64748B' }}>{t('maintenance.import.errors')}</div>
                                </div>
                            </div>
                            {result.errors?.length > 0 && (
                                <div style={{ textAlign: 'left', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '12px', maxHeight: '160px', overflowY: 'auto' }}>
                                    <p style={{ fontWeight: 600, fontSize: '13px', color: '#DC2626', marginBottom: '8px' }}>{t('maintenance.import.errorRows')}</p>
                                    {result.errors.map((err, i) => (
                                        <div key={i} style={{ fontSize: '12px', color: '#7F1D1D', marginBottom: '4px' }}>
                                            <strong>Row {err.row}:</strong> {err.message}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="d-flex justify-content-between align-items-center" style={{ padding: '16px 24px', borderTop: '1px solid #E2E8F0' }}>
                    <button
                        onClick={step === STEP_UPLOAD ? handleClose : () => setStep(s => s - 1)}
                        style={{ padding: '8px 18px', border: '1px solid #E2E8F0', borderRadius: '7px', background: '#FFF', color: '#64748B', cursor: 'pointer', fontSize: '13px' }}
                    >
                        {step === STEP_UPLOAD ? t('common.cancel') : t('common.back')}
                    </button>

                    {step === STEP_MAP && (
                        <button
                            onClick={handlePreview}
                            style={{ padding: '8px 20px', background: '#4F46E5', color: '#FFF', border: 'none', borderRadius: '7px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}
                        >
                            {t('maintenance.import.preview')}
                        </button>
                    )}
                    {step === STEP_PREVIEW && (
                        <button
                            onClick={handleImport}
                            disabled={importing}
                            style={{ padding: '8px 20px', background: '#4F46E5', color: '#FFF', border: 'none', borderRadius: '7px', fontWeight: 600, cursor: 'pointer', fontSize: '13px', opacity: importing ? 0.7 : 1 }}
                        >
                            {importing ? t('common.importing') : t('maintenance.import.importRows', {count: rows.length})}
                        </button>
                    )}
                    {step === STEP_RESULT && (
                        <button
                            onClick={handleClose}
                            style={{ padding: '8px 20px', background: '#22C55E', color: '#FFF', border: 'none', borderRadius: '7px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}
                        >
                            {t('maintenance.import.close')}
                        </button>
                    )}
                    {step === STEP_UPLOAD && <div />}
                </div>
            </div>
        </div>
    );
}
